// src/components/LogsPage.js

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Pagination,
  CircularProgress,
  Alert,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { format } from 'date-fns';
import apiFetch from '../utils/api';

function LogsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [threads, setThreads] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [bots, setBots] = useState([]);
  const [selectedBot, setSelectedBot] = useState(searchParams.get('botId') || '');
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [userIdFilter, setUserIdFilter] = useState(searchParams.get('userId') || '');
  const [page, setPage] = useState(parseInt(searchParams.get('page')) || 1);

  // Fetch available bots
  useEffect(() => {
    async function fetchBots() {
      try {
        const res = await apiFetch('/api/bots', { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          if (Array.isArray(data)) {
            setBots(data);
          } else {
            setBots([...(data.owned || []), ...(data.managed || [])]);
          }
        }
      } catch (err) {
        console.error('Error fetching bots:', err);
      }
    }
    fetchBots();
  }, []);

  const fetchThreads = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      if (selectedBot) params.set('botId', selectedBot);
      if (search) params.set('search', search);
      if (userIdFilter) params.set('userId', userIdFilter);
      params.set('page', page.toString());
      params.set('limit', '25');

      const res = await apiFetch(`/api/logs?${params.toString()}`, {
        credentials: 'include',
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to fetch logs');
      }

      const data = await res.json();
      setThreads(data.threads);
      setTotal(data.total);
      setTotalPages(data.totalPages);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedBot, search, userIdFilter, page]);

  useEffect(() => {
    fetchThreads();
  }, [fetchThreads]);

  // Update URL params when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (selectedBot) params.set('botId', selectedBot);
    if (search) params.set('search', search);
    if (userIdFilter) params.set('userId', userIdFilter);
    if (page > 1) params.set('page', page.toString());
    setSearchParams(params, { replace: true });
  }, [selectedBot, search, userIdFilter, page, setSearchParams]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setPage(1);
    fetchThreads();
  };

  const handleBotChange = (e) => {
    setSelectedBot(e.target.value);
    setPage(1);
  };

  return (
    <Box>
      {/* Page Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" sx={{ mb: 0.5 }}>
          Modmail Logs
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary' }}>
          Search and browse conversation logs from your modmail threads.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3, border: '1px solid', borderColor: 'divider' }} variant="outlined">
        <Box
          component="form"
          onSubmit={handleSearchSubmit}
          sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}
        >
          <FormControl sx={{ minWidth: 200 }} size="small">
            <InputLabel>Bot</InputLabel>
            <Select
              value={selectedBot}
              label="Bot"
              onChange={handleBotChange}
            >
              <MenuItem value="">All Bots</MenuItem>
              {bots.map((bot) => (
                <MenuItem key={bot._id} value={bot._id}>
                  {bot.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            size="small"
            placeholder="Search messages..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 250 }}
          />

          <TextField
            size="small"
            placeholder="User ID"
            value={userIdFilter}
            onChange={(e) => setUserIdFilter(e.target.value)}
            sx={{ minWidth: 180 }}
          />
        </Box>
      </Paper>

      {/* Results */}
      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : threads.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center', border: '1px dashed rgba(255,255,255,0.12)' }} variant="outlined">
          <Typography variant="h6" sx={{ mb: 1 }}>
            No logs found
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {search || userIdFilter ? 'Try adjusting your search filters.' : 'Logs will appear here once your bot handles modmail threads.'}
          </Typography>
        </Paper>
      ) : (
        <>
          <TableContainer component={Paper} variant="outlined" sx={{ border: '1px solid', borderColor: 'divider' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>User</TableCell>
                  <TableCell>User ID</TableCell>
                  <TableCell>Bot</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Messages</TableCell>
                  <TableCell>Visibility</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {threads.map((thread) => (
                  <TableRow
                    key={thread.threadId}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/logs/${thread.threadId}`)}
                  >
                    <TableCell>{thread.threadNumber}</TableCell>
                    <TableCell>{thread.userName}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                        {thread.userId}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {thread.botInstanceId && typeof thread.botInstanceId === 'object'
                        ? thread.botInstanceId.name
                        : '—'}
                    </TableCell>
                    <TableCell>
                      {thread.createdAt
                        ? format(new Date(thread.createdAt), 'MMM d, yyyy HH:mm')
                        : '—'}
                    </TableCell>
                    <TableCell>{thread.messages ? thread.messages.length : '—'}</TableCell>
                    <TableCell>
                      <Chip
                        label={thread.isPublic ? 'Public' : 'Private'}
                        color={thread.isPublic ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {totalPages > 1 && (
            <Box display="flex" justifyContent="center" mt={2}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, value) => setPage(value)}
                color="primary"
              />
            </Box>
          )}

          <Typography variant="body2" color="textSecondary" sx={{ mt: 1, textAlign: 'center' }}>
            {total} total log{total !== 1 ? 's' : ''}
          </Typography>
        </>
      )}
    </Box>
  );
}

export default LogsPage;
